/**
 * Rich page context for the AI Chat Panel (M25.1).
 *
 * M22 shipped the cheap context — `current_url` + `current_entity_id`
 * (see `chat-context.ts`). M25 adds the rich layer so the assistant
 * can answer "what does THIS mean?" about whatever the user is
 * actually looking at:
 *
 *   - `filters`        — the visible filters (read from the URL
 *                        search params — cheap + always accurate).
 *   - `selection`      — ids of rows the user has selected in a table.
 *   - `focused_element`— the element the user clicked "Ask AI" on,
 *                        with its label, value, and source binding
 *                        (set by the M25.2 <Explainable /> affordance).
 *   - `last_action`    — the last mutation the user took on the page
 *                        ("approved leave request RI-204").
 *
 * `selection`, `focused_element`, and `last_action` are pushed in
 * imperatively by pages/components via the setters below — there's
 * no global page-state serializer, just a small module-level store
 * the ChatPanel reads on send. `filters` is derived fresh from the
 * URL every capture.
 *
 * PII (commandment / M25 pitfall): incidental context — `filters`
 * and `last_action` — is run through a redactor that masks obvious
 * secrets (national IDs, IBANs). `focused_element` is NOT redacted:
 * the user explicitly clicked it to ask about it, so masking its
 * value would defeat the feature.
 */

import { captureChatContext } from "./chat-context";

export interface FocusedElement {
  /** Human label for what was clicked ("Remaining annual leave"). */
  label: string;
  /** The displayed value ("18 days"). */
  value: string;
  /** Optional source binding ("leave_balance.remaining_days"). */
  source?: string;
}

export interface RichChatPageContext {
  current_url: string;
  current_entity_id: string | null;
  filters?: Record<string, string>;
  selection?: string[];
  focused_element?: FocusedElement;
  last_action?: string;
}

// ---------------------------------------------------------------------------
// Module-level store — selection / focused element / last action are pushed
// in by the page; filters are always derived fresh from the URL.
// ---------------------------------------------------------------------------

let _selection: string[] | undefined;
let _focusedElement: FocusedElement | undefined;
let _lastAction: string | undefined;

/** Record the rows selected in the current page's table. */
export function setChatSelection(ids: string[] | undefined): void {
  _selection = ids && ids.length > 0 ? ids : undefined;
}

/** Record the element the user clicked "Ask AI" on (M25.2). */
export function setChatFocusedElement(
  el: FocusedElement | undefined,
): void {
  _focusedElement = el;
}

/** Record the last mutation the user took on the page. */
export function setChatLastAction(action: string | undefined): void {
  _lastAction = action;
}

/** Clear the imperative slots — call on navigation away from a page. */
export function clearChatPageContext(): void {
  _selection = undefined;
  _focusedElement = undefined;
  _lastAction = undefined;
}

// ---------------------------------------------------------------------------
// PII redaction — masks obvious secrets in incidental context.
// ---------------------------------------------------------------------------

// A 10-digit run (KSA national id / iqama) or an SA IBAN.
const _NATIONAL_ID_RE = /\b[12]\d{9}\b/g;
const _IBAN_RE = /\bSA\d{22}\b/gi;

function _redact(value: string): string {
  return value
    .replace(_NATIONAL_ID_RE, "[redacted id]")
    .replace(_IBAN_RE, "[redacted iban]");
}

function _redactRecord(
  rec: Record<string, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(rec)) {
    out[k] = _redact(v);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Capture
// ---------------------------------------------------------------------------

/** Read the visible filters from the URL search params. */
function _filtersFromUrl(): Record<string, string> | undefined {
  if (typeof window === "undefined") return undefined;
  const params = new URLSearchParams(window.location.search);
  const filters: Record<string, string> = {};
  for (const [key, value] of params.entries()) {
    if (value) filters[key] = value;
  }
  return Object.keys(filters).length > 0 ? filters : undefined;
}

/**
 * Capture the full rich page context — the M22 basics plus the M25
 * rich layer. Incidental fields are PII-redacted; the focused
 * element is left intact (it's the explicit thing being asked about).
 */
export function captureRichChatContext(): RichChatPageContext {
  const base = captureChatContext();
  const filters = _filtersFromUrl();
  const ctx: RichChatPageContext = {
    current_url: base.current_url,
    current_entity_id: base.current_entity_id,
  };
  if (filters) ctx.filters = _redactRecord(filters);
  if (_selection) ctx.selection = _selection;
  if (_focusedElement) ctx.focused_element = _focusedElement;
  if (_lastAction) ctx.last_action = _redact(_lastAction);
  return ctx;
}
